import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Gates a route behind authentication. Unauthenticated users are sent to
 * /login. This is UI convenience only — the API enforces roles server-side.
 *
 * The gate waits on the SWA principal (`/.auth/me`, edge-served, ~50ms) and
 * NOT on the Editor role (`/api/me`, which can wait out a Function cold start
 * plus an Azure SQL serverless resume). Screens therefore mount — and issue
 * their data queries — while the role is still resolving, instead of after it.
 * See lib/auth.ts for the full rationale.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="auth-splash">
        <span className="auth-splash-dot" />
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
